import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DynamicPageRenderer from "@/components/DynamicPageRenderer";
import type { FallbackSection } from "@/components/DynamicPageRenderer";
import { usePageSEO } from "@/hooks/usePageSEO";
import { useWordPressHomepageFields } from "@/hooks/useWordPress";

// Current homepage section order — used when CMS data is unavailable
const HOMEPAGE_FALLBACK_SECTIONS: FallbackSection[] = [
  { sectionType: 'hero', enabled: true },
  { sectionType: 'work_snapshots', enabled: true },
  { sectionType: 'team_marquee', enabled: true },
  { sectionType: 'linkedin_feed', enabled: true },
  { sectionType: 'vixie_showcase', enabled: true },
  { sectionType: 'featured_events', enabled: true },
  { sectionType: 'trust_logos', enabled: true },
  { sectionType: 'global_map', enabled: true },
  { sectionType: 'conversion_close', enabled: true },
  { sectionType: 'blog', enabled: true },
  { sectionType: 'faq', enabled: true },
  { sectionType: 'patent_disclaimer', enabled: true },
];

// Maps section types to their CMS toggle field names
const HP_TOGGLE_MAP: Record<string, string> = {
  hero: 'heroSectionEnabled',
  work_snapshots: 'workSnapshotsSectionEnabled',
  team_marquee: 'teamMarqueeSectionEnabled',
  vixie_showcase: 'vixieShowcaseSectionEnabled',
  featured_events: 'featuredEventsSectionEnabled',
  trust_logos: 'trustLogosSectionEnabled',
  global_map: 'globalMapSectionEnabled',
  conversion_close: 'conversionCloseSectionEnabled',
  blog: 'blogSectionEnabled',
  faq: 'faqSectionEnabled',
  linkedin_feed: 'linkedinFeedSectionEnabled',
  testimonials: 'testimonialsSectionEnabled',
  patent_disclaimer: 'patentDisclaimerSectionEnabled',
};

function buildToggles(fields: unknown, map: Record<string, string>): Record<string, boolean | undefined> {
  if (!fields) return {};
  const toggles: Record<string, boolean | undefined> = {};
  for (const [sectionType, fieldName] of Object.entries(map)) {
    const val = (fields as Record<string, unknown>)[fieldName];
    if (val !== undefined && val !== null) {
      toggles[sectionType] = Boolean(val);
    }
  }
  return toggles;
}

const Index = () => {
  const { data: fields, isLoading } = useWordPressHomepageFields();

  usePageSEO({
    wpUri: '/home',
    fallbackTitle: 'The Famous Group | Live Event Technology & Fan Experiences',
    fallbackDescription: 'Award-winning creative technology company powering the biggest moments in live sports and entertainment. Mixed reality, fan engagement, and proprietary platforms.',
    fallbackSchema: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'The Famous Group',
      url: 'https://thefamousgroup.com',
      description: 'Award-winning creative technology company powering the biggest moments in live sports and entertainment.',
    },
  });

  return (
    <main className="bg-background min-h-screen">
      <Header />
      <DynamicPageRenderer
        fallbackSections={HOMEPAGE_FALLBACK_SECTIONS}
        sectionToggles={buildToggles(fields, HP_TOGGLE_MAP)}
        cmsLoading={isLoading}
      />
      <Footer />
    </main>
  );
};

export default Index;
